import { useState } from "react";
import { generatePath, useNavigate } from "react-router";
import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";

import { type ErrorType } from "@vega-api/configs/customClientQuery";
import {
  getGetDealShortlistQueryKey,
  useGetDealShortlist,
} from "@vega-api/generated/coinvest-deal-shortlist/coinvest-deal-shortlist";
import {
  getGetDealTeaserEmailsV2QueryKey,
  useGetDealTeaserEmailsV2,
  useScheduleDealShortlistTeaserEmailsV2,
  useSendDealShortlistTeaserEmailsV2,
} from "@vega-api/generated/coinvest-deal-teaser-email-v2/coinvest-deal-teaser-email-v2";
import { type TeaserEmail } from "@vega-api/generated/types/teaserEmail";
import { TeaserEmailStatus } from "@vega-api/generated/types/teaserEmailStatus";
import { useToast } from "@vega-core/components/Toast/useToast";
import { m } from "@vega-core/paraglide/messages";
import { parseUtcDate } from "@vega-core/utils/formatDate";
import { useCoInvestErrorToast } from "@vega-modules/hooks/useCoInvestErrorToast";
import { CoInvestmentFullPaths } from "@vega-modules/paths";

import { DRAFT_AUTOSAVE_MUTATION_KEY } from "../DraftEmail/draftEmailUtils";

const APPROVED_SHORTLIST_PAGE_SIZE = 500;

export type SendDraftRowModel = {
  draft: TeaserEmail;
  lpName: string;
  limitedPartnerId: string;
  hasRecipients: boolean;
};

const buildSendableSelection = (rows: SendDraftRowModel[]): Set<string> =>
  new Set(rows.filter((row) => row.hasRecipients).map((row) => row.draft.id));

const toSelectAllState = (
  allSelected: boolean,
  someSelected: boolean,
): boolean | "indeterminate" => {
  if (allSelected) {
    return true;
  }

  if (someSelected) {
    return "indeterminate";
  }

  return false;
};

export function useSendDraftsManagement(dealId: string) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const showToast = useToast();
  const showErrorToast = useCoInvestErrorToast();

  const { data: drafts = [], isPending: isDraftsLoading } =
    useGetDealTeaserEmailsV2(
      dealId,
      { statuses: [TeaserEmailStatus.DRAFT, TeaserEmailStatus.SCHEDULED] },
      {
        query: {
          enabled: !!dealId,
          select: (response) => response.data,
        },
      },
    );

  const { data: approvedItems = [], isPending: isShortlistLoading } =
    useGetDealShortlist(
      dealId,
      { isApproved: true, page: 1, pageSize: APPROVED_SHORTLIST_PAGE_SIZE },
      {
        query: {
          enabled: !!dealId,
          select: (response) => response.data?.dealShortlistItems ?? [],
        },
      },
    );

  const lpByShortlistItemId = new Map(
    approvedItems.map((item) => [
      item.id,
      {
        name: item.limitedPartner.name,
        limitedPartnerId: item.limitedPartner.id,
      },
    ]),
  );

  const rows: SendDraftRowModel[] = drafts.map((draft) => {
    const lp = lpByShortlistItemId.get(draft.dealShortlistItemId);

    return {
      draft,
      lpName: lp?.name ?? m.coInvestments_sendDrafts_unknownLp(),
      limitedPartnerId: lp?.limitedPartnerId ?? "",
      hasRecipients: (draft.recipients?.length ?? 0) > 0,
    };
  });

  const [selectedIds, setSelectedIds] = useState<Set<string> | null>(null);
  const effectiveSelected = selectedIds ?? buildSendableSelection(rows);

  const sendableIds = rows
    .filter((row) => row.hasRecipients && effectiveSelected.has(row.draft.id))
    .map((row) => row.draft.id);

  const isAutoSaving =
    useIsMutating({ mutationKey: DRAFT_AUTOSAVE_MUTATION_KEY }) > 0;

  const sendableRowCount = rows.filter((row) => row.hasRecipients).length;
  const allSelected =
    sendableRowCount > 0 && sendableIds.length === sendableRowCount;
  const someSelected = sendableIds.length > 0 && !allSelected;

  const toggleSelected = (draftId: string, checked: boolean) => {
    const next = new Set(effectiveSelected);

    if (checked) {
      next.add(draftId);
    } else {
      next.delete(draftId);
    }

    setSelectedIds(next);
  };

  const toggleSelectAll = (checked: boolean) => {
    setSelectedIds(checked ? buildSendableSelection(rows) : new Set());
  };

  const invalidateDrafts = () =>
    queryClient.invalidateQueries({
      queryKey: getGetDealTeaserEmailsV2QueryKey(dealId),
    });

  const navigateToShare = () =>
    navigate(
      generatePath(CoInvestmentFullPaths.CoInvestmentShare, { id: dealId }),
    );

  const { mutate: sendEmails, isPending: isSending } =
    useSendDealShortlistTeaserEmailsV2({
      mutation: {
        onSuccess: () => {
          showToast({
            variant: "success",
            title: m.coInvestments_sendDrafts_toast_sent(),
          });
          invalidateDrafts();
          queryClient.invalidateQueries({
            queryKey: getGetDealShortlistQueryKey(dealId),
          });
          navigateToShare();
        },
        onError: (error: ErrorType<unknown>) => showErrorToast(error),
      },
    });

  const sendSelected = () => {
    if (sendableIds.length === 0) {
      return;
    }

    sendEmails({ dealId, data: { emailIds: sendableIds } });
  };

  const { mutate: scheduleEmails, isPending: isScheduling } =
    useScheduleDealShortlistTeaserEmailsV2({
      mutation: {
        onSuccess: (_response, { data }) => {
          const count = data.emails.length;
          const dateTime = format(
            parseUtcDate(data.emails[0].scheduledAt),
            "EEE d MMM, p",
          );

          showToast({
            variant: "success",
            title:
              count === 1
                ? m.coInvestments_sendDrafts_toast_scheduledOne({ dateTime })
                : m.coInvestments_sendDrafts_toast_scheduledOther({
                    count,
                    dateTime,
                  }),
          });
          invalidateDrafts();
          queryClient.invalidateQueries({
            queryKey: getGetDealShortlistQueryKey(dealId),
          });
          navigateToShare();
        },
        onError: (error: ErrorType<unknown>) => showErrorToast(error),
      },
    });

  const scheduleSelected = (scheduledAt: string) => {
    if (sendableIds.length === 0) {
      return;
    }

    scheduleEmails({
      dealId,
      data: {
        emails: sendableIds.map((emailId) => ({ emailId, scheduledAt })),
      },
    });
  };

  return {
    rows,
    isLoading: isDraftsLoading || isShortlistLoading,
    selectedIds: effectiveSelected,
    sendableIds,
    selectAllState: toSelectAllState(allSelected, someSelected),
    toggleSelected,
    toggleSelectAll,
    sendSelected,
    isSending,
    scheduleSelected,
    isScheduling,
    isAutoSaving,
    onDraftSaved: invalidateDrafts,
    navigateToShare,
  };
}
